import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import StudentInsightsCharts from "./StudentInsightsCharts";
import "./ActiveSignOuts.css";

// A trip counts as a "bathroom" trip if its location mentions a restroom.
const isBathroom = (loc = "") => /bathroom|restroom|toilet|washroom/i.test(loc);

// Duration of a completed trip in whole minutes, or null if still out / no time_in.
function durationMins(rec) {
  if (!rec.time_in || !rec.time_out) return null;
  const ms = new Date(rec.time_in).getTime() - new Date(rec.time_out).getTime();
  return ms > 0 ? Math.round(ms / 60000) : null;
}

function fmtMins(m) {
  if (m == null) return "—";
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

// A student key that survives name typos: prefer the ID, fall back to the name.
const studentKey = (r) => r.idNumber || r.studentName || "Unknown";

const RANGES = {
  all: "All time",
  thisSchoolYear: "This school year",
  thisMonth: "This month",
  thisWeek: "This week",
};

function rangeStart(key) {
  const now = new Date();
  switch (key) {
    case "thisWeek": {
      const d = new Date(now);
      d.setDate(d.getDate() - d.getDay());
      d.setHours(0, 0, 0, 0);
      return d;
    }
    case "thisMonth":
      return new Date(now.getFullYear(), now.getMonth(), 1);
    case "thisSchoolYear":
      // School year starts in August.
      return new Date(now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1, 7, 1);
    default:
      return null; // all time
  }
}

const StatCard = ({ label, value, sub }) => (
  <div
    style={{
      flex: "1 1 150px",
      minWidth: 150,
      padding: "16px 18px",
      background: "var(--color-white, #fff)",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    }}
  >
    <div style={{ fontSize: 13, color: "var(--color-secondary)", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "var(--color-secondary)", marginTop: 4 }}>{sub}</div>}
  </div>
);

export default function StudentInsights() {
  const { uid } = useUser();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const [range, setRange] = useState("thisSchoolYear");

  // The teacher's own sign-out history is the only per-student data the rules let us
  // read (other teachers' history is owner/super-admin only), so insights here cover
  // trips this teacher signed the student out for.
  useEffect(() => {
    if (!uid) return;
    let alive = true;
    (async () => {
      try {
        const snap = await getDocs(collection(firestore, "users", uid, "signedInHistory"));
        if (alive) setHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error loading sign-out history:", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [uid]);

  // Apply the date-range filter once, up front.
  const inRange = useMemo(() => {
    const start = rangeStart(range);
    if (!start) return history;
    return history.filter((r) => r.time_out && new Date(r.time_out) >= start);
  }, [history, range]);

  // One row per student: trip count + display name, sorted most-active first.
  const students = useMemo(() => {
    const byKey = {};
    for (const r of inRange) {
      const key = studentKey(r);
      if (!byKey[key]) {
        byKey[key] = { key, name: r.studentName || "Unknown", idNumber: r.idNumber || "", grade: r.grade || "", count: 0 };
      }
      byKey[key].count += 1;
    }
    return Object.values(byKey).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [inRange]);

  const visibleStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (s) => s.name.toLowerCase().includes(q) || String(s.idNumber).toLowerCase().includes(q)
    );
  }, [students, search]);

  const selected = students.find((s) => s.key === selectedKey) || null;

  const records = useMemo(
    () => (selected ? inRange.filter((r) => studentKey(r) === selected.key) : []),
    [inRange, selected]
  );

  const stats = useMemo(() => {
    if (!records.length) return null;
    const total = records.length;
    const bathroom = records.filter((r) => isBathroom(r.location)).length;
    const durations = records.map(durationMins).filter((m) => m != null);
    const avg = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : null;
    const longest = durations.length ? Math.max(...durations) : null;

    const byLoc = {};
    records.forEach((r) => {
      const l = r.location || "Unknown";
      byLoc[l] = (byLoc[l] || 0) + 1;
    });
    const topLocation = Object.entries(byLoc).sort((a, b) => b[1] - a[1])[0];

    const byHour = {};
    records.forEach((r) => {
      if (!r.time_out) return;
      const h = new Date(r.time_out).getHours();
      byHour[h] = (byHour[h] || 0) + 1;
    });
    const busiest = Object.entries(byHour).sort((a, b) => b[1] - a[1])[0];
    const fmtHour = (h) => `${(h % 12) || 12}:00 ${h >= 12 ? "PM" : "AM"}`;

    return {
      total,
      bathroom,
      bathroomPct: Math.round((bathroom / total) * 100),
      avg,
      longest,
      topLocation: topLocation ? `${topLocation[0]} (${topLocation[1]})` : "—",
      busiest: busiest ? `${fmtHour(Number(busiest[0]))} (${busiest[1]})` : "—",
    };
  }, [records]);

  const recent = useMemo(
    () => [...records].sort((a, b) => new Date(b.time_out) - new Date(a.time_out)).slice(0, 15),
    [records]
  );

  if (loading) return <div className="table-container"><h1>Student Insights</h1><p>Loading…</p></div>;

  return (
    <div className="table-container">
      <h1>Student Insights</h1>
      <p style={{ color: "var(--color-secondary)", marginTop: -8, marginBottom: 20 }}>
        Pick a student to see how often and when they leave the room, where they go, and how long
        they're gone — based on the sign-outs you've recorded.
      </p>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 20 }}>
        <input
          type="text"
          placeholder="Search students by name or ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: "1 1 220px", maxWidth: 320 }}
        />
        <select value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)} style={{ flex: "1 1 220px", maxWidth: 360 }}>
          <option value="">Select a student…</option>
          {visibleStudents.map((s) => (
            <option key={s.key} value={s.key}>
              {s.name}{s.idNumber ? ` · #${s.idNumber}` : ""} — {s.count} trip{s.count !== 1 ? "s" : ""}
            </option>
          ))}
        </select>
        <select value={range} onChange={(e) => setRange(e.target.value)}>
          {Object.entries(RANGES).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
      </div>

      {!selected ? (
        <p style={{ color: "var(--color-secondary)" }}>
          {students.length === 0
            ? "No sign-out history yet for this date range."
            : `${students.length} student${students.length !== 1 ? "s" : ""} with sign-outs in this range. Choose one above.`}
        </p>
      ) : !stats ? (
        <p style={{ color: "var(--color-secondary)" }}>No trips for {selected.name} in this date range.</p>
      ) : (
        <>
          <h2 style={{ marginBottom: 12 }}>
            {selected.name}
            {selected.grade && <span style={{ fontSize: 15, fontWeight: 400, color: "var(--color-secondary)", marginLeft: 10 }}>Grade {selected.grade}</span>}
          </h2>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
            <StatCard label="Total trips" value={stats.total} />
            <StatCard label="Bathroom trips" value={stats.bathroom} sub={`${stats.bathroomPct}% of all trips`} />
            <StatCard label="Avg. time out" value={fmtMins(stats.avg)} />
            <StatCard label="Longest trip" value={fmtMins(stats.longest)} />
            <StatCard label="Most frequent" value={stats.topLocation} />
            <StatCard label="Busiest hour" value={stats.busiest} />
          </div>

          <StudentInsightsCharts records={records} />

          <h2 style={{ marginTop: 12 }}>Recent trips</h2>
          <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Location</th>
                <th>Time Out</th>
                <th>Time In</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.time_out).toLocaleDateString()}</td>
                  <td>{r.location}</td>
                  <td>{new Date(r.time_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                  <td>{r.time_in ? new Date(r.time_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td>{fmtMins(durationMins(r))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
}
